import { PAYMENTS } from 'state/payment/type';
import { ToastType } from 'components/toasts/container';

/**
 * Toast type enum for transaction-related notifications
 */
export enum TransactionToastType {
  CASH = 'CASH',
  RETRY = 'SINGLE_RETRY',
  CANCEL = 'CANCEL',
  REFUND = 'REFUND',
  FAILED_RETRY = 'FAILED_RETRY',
  FAILED_CASH = 'FAILED_CASH',
  FAILED_CANCEL = 'FAILED_CANCEL',
  FAILED_REFUND = 'FAILED_REFUND',
}

/**
 * Action types available for transactions
 */
export enum TransactionActionType {
  VERIFY_SALE = 'VERIFY_SALE',
  RECORD_SALE = 'RECORD_SALE',
  REFUND = 'REFUND',
}

/**
 * Props for the main TransactionDetailsTab component
 */
export interface TransactionDetailsTabProps {
  dateFrom: string;
  dateTo: string;
  triggerRefresh: boolean;
  isActiveTab: boolean;
}

/**
 * Extended transaction data with computed fields for table display
 */
export interface TransactionTableData extends PAYMENTS.ITransactionResult {
  customerName: string;
  grossAmount: number;
  transactionDate: string;
}

/**
 * Props for the TransactionActionsMenu component
 */
export interface TransactionActionsMenuProps {
  row: TransactionTableData;
  onVerifySale: (row: TransactionTableData) => void;
  onRecordSale: (row: TransactionTableData) => void;
  onRefund: (row: TransactionTableData) => void;
  canRecordSale: boolean;
}

/**
 * Parameters for fetching transaction data
 */
export interface TransactionFetchParams {
  key?: string | null;
  start_date?: string;
  end_date?: string;
  amount_1?: string;
  amount_2?: string;
  condition?: string;
  status?: string;
  payment_type?: string;
  selected_entity_ids?: string;
  search_string?: string;
}

/**
 * Return type for useTransactionDetails hook
 */
export interface UseTransactionDetailsReturn {
  tableData: TransactionTableData[];
  isLoading: boolean;
  isEmpty: boolean;
  prevKey: string | null;
  nextKey: string | null;
  numResults: number;
  prevPage: () => void;
  nextPage: () => void;
  fetchTransactions: (params?: Partial<TransactionFetchParams>) => void;
}

/**
 * Return type for useTransactionActions hook
 */
export interface UseTransactionActionsReturn {
  toastType: TransactionToastType | null;
  warningType: ToastType | undefined;
  toastMessage: string;
  toastSubDetails: JSX.Element | undefined;
  handleCancel: (row: TransactionTableData) => Promise<void>;
  handleRefund: (row: TransactionTableData) => Promise<void>;
  clearToast: () => void;
}

/**
 * Parameters for creating column configurations
 */
export interface TransactionColumnsParams {
  settings: { timezone?: string } | null;
  isOrgLevel: boolean;
  isActiveTab: boolean;
  getStoreName: (row: TransactionTableData) => string;
  isGatewayTransactionID: (id: string) => boolean;
  onVerifySale: (row: TransactionTableData) => void;
  onRecordSale: (row: TransactionTableData) => void;
  onRefund: (row: TransactionTableData) => void;
  canRecordSale: boolean;
}

/**
 * @deprecated Use TransactionTableData instead
 * Backwards compatible alias for ITableData
 */
export type ITableData = TransactionTableData;
